import { breakpoints, InfoButton, Inline, Stack, Text, textColors, textVariants } from '@etg/wings';
import { ContentWrapper, ScreenReaderPrice } from '@eti/components';
import { price as priceUtils } from '@eti/utils';
import { css, etiColors } from '@eti/styles';
import { useMediaQuery } from 'usehooks-ts';
import { useProperty, useTranslation } from '@eti/providers';

const mobileButtonContainerStyles = css`
  background-color: ${etiColors.white};
  height: 40px;
  width: 100%;
`;

const contentWrapperStyles = css`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

const mobileStyles = css`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

interface EtgFaresPriceProps {
  handlePriceModalOpen: () => void;
  includedInTripProductPrice: number;
  numberOfTravelersWithoutInfants: number;
  selectedBundlePrice: number;
  totalTripPrice: number;
  tripPricePerAdult: number;
}

const { formatPrice, getPriceDivided } = priceUtils;

const dividedPrice = (
  combinedExtraServicesPrice: number,
  numberOfTravelersWithoutInfants: number,
) => getPriceDivided(combinedExtraServicesPrice, numberOfTravelersWithoutInfants) || 0;

const EtgFaresPrice = ({
  handlePriceModalOpen,
  includedInTripProductPrice,
  numberOfTravelersWithoutInfants,
  selectedBundlePrice,
  totalTripPrice,
  tripPricePerAdult,
}: EtgFaresPriceProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints._768})`);

  const pricePerTraveler = p('Bundle.Overlay.Price.Per.Person.Enabled');
  const priceText = pricePerTraveler
    ? t('Price.Per.Person')
    : t('Product.FlexibleTicket.Bundle.Price.Text');
  const priceTextMobile = t('Product.ETG.Fares.Person.Text');
  const infoButtonLabel = t('ScreenReader.PriceSpecification.InfoButton');

  const combinedExtraServicesPrice = includedInTripProductPrice + selectedBundlePrice;
  const totalPrice = pricePerTraveler
    ? tripPricePerAdult + dividedPrice(combinedExtraServicesPrice, numberOfTravelersWithoutInfants)
    : totalTripPrice + combinedExtraServicesPrice;

  return isDesktop ? (
    <Stack
      align="end"
      aria-labelledby="totalPriceText priceToPay priceToPayText"
      data-testid="etg-fares-desktop-price"
      spacing={0}
    >
      <Inline spacing={4}>
        <InfoButton label={infoButtonLabel} onClick={handlePriceModalOpen} />
        <Text aria-hidden="true" variant={textVariants._24_EMPHASIZED}>
          {formatPrice(totalPrice)}
        </Text>
        <ScreenReaderPrice id="priceToPay" price={formatPrice(totalPrice)} />
      </Inline>
      <Text aria-hidden="true" color={textColors.SECONDARY} id="priceToPayText">
        {priceText}
      </Text>
    </Stack>
  ) : (
    <Inline
      aria-labelledby="totalPriceText priceToPay priceToPayText"
      className={mobileButtonContainerStyles}
      noWrap
      spacing={8}
    >
      <ContentWrapper className={contentWrapperStyles}>
        <Inline
          alignY="center"
          className={mobileStyles}
          data-testid="etg-fares-mobile-price"
          spacing={0}
        >
          <Inline alignY="center" spacing={4}>
            <Text aria-hidden="true" id="totalPriceText">
              {t('Product.ETG.Fares.Total.Price')}
            </Text>
            <InfoButton label={infoButtonLabel} onClick={handlePriceModalOpen} />
          </Inline>
          <Inline alignY="center" spacing={4}>
            <Text aria-hidden="true" variant={textVariants._18_EMPHASIZED}>
              {formatPrice(totalPrice)}
            </Text>
            <ScreenReaderPrice id="priceToPay" price={formatPrice(totalPrice)} />
            <Text aria-hidden="true" color={textColors.SECONDARY} id="priceToPayText">
              {pricePerTraveler ? priceTextMobile : ''}
            </Text>
          </Inline>
        </Inline>
      </ContentWrapper>
    </Inline>
  );
};

export default EtgFaresPrice;
